// src/pwa.js
import { registerSW } from 'virtual:pwa-register';
import { useCallback, useEffect, useRef, useState } from 'react';

const subscribers = new Set();
let swRegistered = false;
let installListenersRegistered = false;
let swUpdate = async () => {};
let installPromptEvent = null;
let sharedState = {
  offlineReady: false,
  needRefresh: false,
  canPromptInstall: false,
  isInstalled: false,
};

const emitSharedState = () => {
  subscribers.forEach((listener) => {
    try {
      listener(sharedState);
    } catch {
      // ignore subscriber failures
    }
  });
};

const patchSharedState = (patch = {}) => {
  sharedState = {
    ...sharedState,
    ...patch,
  };
  emitSharedState();
};

const ensureSwRegistered = () => {
  if (swRegistered) return;
  swRegistered = true;

  swUpdate = registerSW({
    immediate: true,
    onOfflineReady() {
      patchSharedState({ offlineReady: true });
    },
    onNeedRefresh() {
      patchSharedState({ needRefresh: true });
    },
  });
};

const isStandaloneApp = () => (
  (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
  (('standalone' in navigator) && navigator.standalone)
);

const syncInstalledState = () => {
  if (typeof window === 'undefined') return;
  patchSharedState({ isInstalled: Boolean(isStandaloneApp()) });
};

const onBeforeInstallPrompt = (event) => {
  event.preventDefault();
  installPromptEvent = event;
  patchSharedState({
    canPromptInstall: true,
    isInstalled: Boolean(isStandaloneApp()),
  });
};

const onAppInstalled = () => {
  installPromptEvent = null;
  patchSharedState({
    canPromptInstall: false,
    isInstalled: true,
  });
};

const ensureInstallPromptListeners = () => {
  if (installListenersRegistered || typeof window === 'undefined') return;
  installListenersRegistered = true;

  syncInstalledState();
  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.addEventListener('appinstalled', onAppInstalled);
  window.addEventListener('pageshow', syncInstalledState);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') syncInstalledState();
  });
};

const ensurePwaRuntime = () => {
  ensureSwRegistered();
  ensureInstallPromptListeners();
};

const triggerInstallPrompt = async () => {
  const deferred = installPromptEvent;
  if (!deferred) return { outcome: 'unavailable' };

  deferred.prompt();
  const choice = await deferred.userChoice.catch(() => null);
  installPromptEvent = null;
  patchSharedState({ canPromptInstall: false });
  if (choice?.outcome === 'accepted') {
    patchSharedState({ isInstalled: true });
  }
  return choice || { outcome: 'dismissed' };
};

export function initPwa() {
  ensurePwaRuntime();
}

export function usePwaUpdater() {
  const mountedRef = useRef(false);
  const [offlineReady, setOfflineReady] = useState(sharedState.offlineReady);
  const [needRefresh, setNeedRefresh] = useState(sharedState.needRefresh);

  useEffect(() => {
    mountedRef.current = true;
    ensurePwaRuntime();

    const listener = (nextState) => {
      if (!mountedRef.current) return;
      setOfflineReady(Boolean(nextState?.offlineReady));
      setNeedRefresh(Boolean(nextState?.needRefresh));
    };

    subscribers.add(listener);
    listener(sharedState);

    return () => {
      mountedRef.current = false;
      subscribers.delete(listener);
    };
  }, []);

  const close = useCallback(() => {
    patchSharedState({
      offlineReady: false,
      needRefresh: false,
    });
  }, []);

  const updateServiceWorker = useCallback((reloadPage = true) => {
    return swUpdate?.(reloadPage) || Promise.resolve();
  }, []);

  return {
    offlineReady,
    needRefresh,
    updateServiceWorker,
    close,
  };
}

export function usePwaInstallPrompt() {
  const mountedRef = useRef(false);
  const [canPromptInstall, setCanPromptInstall] = useState(sharedState.canPromptInstall);
  const [isInstalled, setIsInstalled] = useState(sharedState.isInstalled);

  useEffect(() => {
    mountedRef.current = true;
    ensurePwaRuntime();

    const listener = (nextState) => {
      if (!mountedRef.current) return;
      setCanPromptInstall(Boolean(nextState?.canPromptInstall));
      setIsInstalled(Boolean(nextState?.isInstalled));
    };

    subscribers.add(listener);
    listener(sharedState);

    return () => {
      mountedRef.current = false;
      subscribers.delete(listener);
    };
  }, []);

  const promptInstall = useCallback(() => {
    return triggerInstallPrompt();
  }, []);

  return {
    canPromptInstall,
    isInstalled,
    promptInstall,
  };
}
